import { FC, useEffect, useMemo, useRef } from 'react';
import {
    useTranslate,
    IResourceComponentsProps,
    HttpError,
    CrudFilters,
} from '@refinedev/core';
import { Table, Form } from 'antd';
import type { TablePaginationConfig } from 'antd';
import {
    IColumn,
    IRecordFilterVariables,
    IApplication,
} from 'interfaces/application.interface';
import { containsExpert } from '../../../utils/roles';
import { TextField, useTable, EditButton } from '@refinedev/antd';
import WidthLimiter from '../../../components/width-limiter';
import { joinObjectValues } from '../../../utils/format';
import styles from './list.module.scss';
import { CreateButton } from '../../../components/ui/create-button';
import { Heading } from '../../../components/ui/heading';
import { useDictionarySelectProps } from '../../../hooks/applications/useDictionarySelectProps';
import type { FormInstance } from 'antd';
import { ListLayout } from '../../../layouts/ListLayout';
import {
    FilterControls,
    type FilterDropdownOption,
} from '../../../components/applications/filter-controls';
import { StatusTagField } from '../../../components/ui/status-tag';
import TablePagination from '../../../components/ui/table-pagination';

type ExtendedPaginationConfig = TablePaginationConfig & {
    pageSizeSelectorWidth?: number | string;
};

export const ApplicationList: FC<IResourceComponentsProps> = () => {
    const t = useTranslate();
    const {
        tableProps,
        tableQueryResult,
        searchFormProps,
        filters: activeFilters,
        setFilters,
        current,
        setCurrent,
        pageSize,
        setPageSize,
    } = useTable<IApplication, HttpError, IRecordFilterVariables>({
        filters: {
            defaultBehavior: 'replace',
        },
        onSearch: params => {
            const { status_id, type_id, query } = params;
            const filters: CrudFilters = [];

            const addFilter = (field: string, value: unknown) => {
                if (
                    value === undefined ||
                    value === null ||
                    (typeof value === 'string' && value.trim() === '')
                ) {
                    return;
                }

                filters.push({ field, operator: 'eq', value });
            };

            addFilter('query', query);
            addFilter('status_id', status_id);
            addFilter('type_id', type_id);

            return filters;
        },
    });

    const isAdmin = containsExpert();
    const { applicationTypeProps, applicationStatusProps } =
        useDictionarySelectProps();
    const formInstance = searchFormProps.form as FormInstance | undefined;
    const queryValue = Form.useWatch('query', formInstance);
    const typeValue = Form.useWatch('type_id', formInstance);
    const statusValue = Form.useWatch('status_id', formInstance);
    const { pagination: rawPagination, ...restTableProps } = tableProps;
    const pagination =
        rawPagination === false
            ? undefined
            : (rawPagination as ExtendedPaginationConfig);

    const typeOptions = useMemo(() => {
        return (applicationTypeProps?.options ?? []) as FilterDropdownOption[];
    }, [applicationTypeProps]);

    const statusOptions = useMemo(() => {
        return (applicationStatusProps?.options ??
            []) as FilterDropdownOption[];
    }, [applicationStatusProps]);

    const originalTypeOnChange = applicationTypeProps?.onChange;
    const originalStatusOnChange = applicationStatusProps?.onChange;

    const clearLabelText = t('applications.filters.clear');

    const removeFilterByField = (field: string) => {
        setFilters(prevFilters =>
            prevFilters.filter(filter => {
                if (!('field' in filter)) return true;

                return filter.field !== field;
            })
        );
    };

    const handleTypeSelect = (
        nextValue: string | number | undefined,
        option?: FilterDropdownOption
    ) => {
        if (!formInstance) return;

        const isResetValue =
            nextValue === undefined ||
            nextValue === null ||
            (typeof nextValue === 'string' && nextValue.trim() === '');

        if (isResetValue) {
            originalTypeOnChange?.(undefined as any, undefined as any);
            removeFilterByField('type_id');
            formInstance.submit();
            return;
        }

        const resolvedOption =
            option ??
            typeOptions.find(opt => String(opt.value) === String(nextValue));

        if (resolvedOption) {
            originalTypeOnChange?.(
                resolvedOption.value as any,
                resolvedOption as any
            );
        } else {
            originalTypeOnChange?.(nextValue as any, undefined as any);
        }

        formInstance.submit();
    };

    const handleStatusSelect = (
        nextValue: string | number | undefined,
        option?: FilterDropdownOption
    ) => {
        if (!formInstance) return;

        const isResetValue =
            nextValue === undefined ||
            nextValue === null ||
            (typeof nextValue === 'string' && nextValue.trim() === '');

        if (isResetValue) {
            originalStatusOnChange?.(undefined as any, undefined as any);
            removeFilterByField('status_id');
            formInstance.submit();
            return;
        }

        const resolvedOption =
            option ??
            statusOptions.find(opt => String(opt.value) === String(nextValue));

        if (resolvedOption) {
            originalStatusOnChange?.(
                resolvedOption.value as any,
                resolvedOption as any
            );
        } else {
            originalStatusOnChange?.(nextValue as any, undefined as any);
        }

        formInstance.submit();
    };

    const applyTypeSelection = (
        nextValue: string | number | undefined,
        option?: FilterDropdownOption
    ) => {
        if (!formInstance) return;

        formInstance.setFieldsValue({ type_id: nextValue });
        handleTypeSelect(nextValue, option);
    };

    const applyStatusSelection = (
        nextValue: string | number | undefined,
        option?: FilterDropdownOption
    ) => {
        if (!formInstance) return;

        formInstance.setFieldsValue({ status_id: nextValue });
        handleStatusSelect(nextValue, option);
    };

    const clearedInitialFilters = useRef(false);
    const isInitialQueryWatch = useRef(true);

    useEffect(() => {
        if (!formInstance || clearedInitialFilters.current) return;

        const hasInitialFilter = activeFilters.some(filter => {
            if (!('field' in filter)) return false;

            return filter.field === 'type_id' || filter.field === 'status_id';
        });

        if (hasInitialFilter) {
            setFilters(prevFilters =>
                prevFilters.filter(filter => {
                    if (!('field' in filter)) return true;

                    return (
                        filter.field !== 'type_id' &&
                        filter.field !== 'status_id'
                    );
                })
            );
        }

        formInstance.setFieldsValue({
            type_id: undefined,
            status_id: undefined,
        });

        clearedInitialFilters.current = true;
    }, [activeFilters, formInstance, setFilters]);

    const isAllActive =
        (typeValue === undefined || typeValue === null || typeValue === '') &&
        (statusValue === undefined ||
            statusValue === null ||
            statusValue === '');

    const handleResetFilters = () => {
        if (!formInstance) return;

        formInstance.setFieldsValue({
            type_id: undefined,
            status_id: undefined,
        });

        originalTypeOnChange?.(undefined as any, undefined as any);
        originalStatusOnChange?.(undefined as any, undefined as any);

        setFilters(prevFilters =>
            prevFilters.filter(filter => {
                if (!('field' in filter)) return true;

                return (
                    filter.field !== 'type_id' && filter.field !== 'status_id'
                );
            })
        );
        formInstance.submit();
    };

    useEffect(() => {
        if (!formInstance) return;

        if (isInitialQueryWatch.current) {
            isInitialQueryWatch.current = false;
            return;
        }

        const handler = window.setTimeout(() => {
            formInstance.submit();
        }, 300);

        return () => window.clearTimeout(handler);
    }, [formInstance, queryValue]);

    const currentPage = current ?? 1;
    const resolvedPageSize = pageSize ?? pagination?.pageSize ?? 10;
    const totalItems = tableQueryResult?.data?.total ?? pagination?.total ?? 0;

    const defaultPageSizeOptions = ['10', '25', '50', '100'];
    const rawPageSizeOptions = (
        pagination?.pageSizeOptions ?? defaultPageSizeOptions
    ).map(option => option.toString());

    const uniquePageSizeOptions = rawPageSizeOptions.reduce<string[]>(
        (acc, current) => {
            if (!acc.includes(current)) acc.push(current);
            return acc;
        },
        []
    );

    const orderedPageSizeOptions = defaultPageSizeOptions.filter(option =>
        uniquePageSizeOptions.includes(option)
    );

    uniquePageSizeOptions.forEach(option => {
        if (!orderedPageSizeOptions.includes(option)) {
            orderedPageSizeOptions.push(option);
        }
    });

    if (!orderedPageSizeOptions.includes(resolvedPageSize.toString())) {
        orderedPageSizeOptions.push(resolvedPageSize.toString());
    }

    const handlePageChange = (page: number) => {
        setCurrent?.(page);
    };

    const handlePageSizeChange = (value: number) => {
        if (Number.isNaN(value)) return;

        setPageSize?.(value);
        setCurrent?.(1);
    };

    const pageSizeSelectorWidth = pagination?.pageSizeSelectorWidth;
    const resolvedPageSizeSelectorWidth =
        typeof pageSizeSelectorWidth === 'number'
            ? `${pageSizeSelectorWidth}px`
            : pageSizeSelectorWidth;
    return (
        <ListLayout>
            <div className={styles.pageWrapper}>
                <header className={styles.header}>
                    <Heading level={1} align="center" className={styles.title}>
                        {t('applications.applications')}
                    </Heading>
                    <FilterControls
                        formProps={searchFormProps}
                        hiddenFieldNames={['type_id', 'status_id']}
                        searchPlaceholder={t('applications.fields.trade_name')}
                        filtersLabel={t('applications.titles.filters')}
                        clearFilterLabel={clearLabelText}
                        isAllActive={isAllActive}
                        onResetFilters={handleResetFilters}
                        dropdowns={[
                            {
                                placeholder: t('applications.fields.type'),
                                value: typeValue,
                                options: typeOptions,
                                onSelect: applyTypeSelection,
                                clearLabel: clearLabelText,
                                showSearch: true,
                                onSearch: value =>
                                    applicationTypeProps?.onSearch?.(value),
                                loading: applicationTypeProps?.loading,
                                searchPlaceholder: t(
                                    'applications.fields.type'
                                ),
                            },
                            {
                                placeholder: t(
                                    'applications.fields.ectd_dossier_status'
                                ),
                                value: statusValue,
                                options: statusOptions,
                                onSelect: applyStatusSelection,
                                clearLabel: clearLabelText,
                                showSearch: true,
                                onSearch: value =>
                                    applicationStatusProps?.onSearch?.(value),
                                loading: applicationStatusProps?.loading,
                                searchPlaceholder: t(
                                    'applications.fields.ectd_dossier_status'
                                ),
                            },
                        ]}
                        extraActions={
                            isAdmin && (
                                <div className={styles.createButtonWrapper}>
                                    <CreateButton
                                        resource="applications"
                                        label={t(
                                            'applications.buttons.create_an_application_with_ectd'
                                        )}
                                        className={styles.createButton}
                                        variant="primary"
                                    />
                                </div>
                            )
                        }
                    />
                </header>
                <div className={styles.tableWrapper}>
                    <Table
                        {...restTableProps}
                        pagination={false}
                        rowKey="id"
                        className={styles.table}
                        tableLayout="fixed"
                    >
                        <Table.Column
                            dataIndex="name"
                            key="name"
                            title={t('applications.fields.trade_name')}
                            render={(value, row: IColumn) => (
                                <div className={styles.cellContent}>
                                    <WidthLimiter>
                                        <TextField value={value} />
                                        <br />
                                        <span
                                            className={styles.applicationGuid}
                                        >
                                            <TextField value={row.guid} />
                                        </span>
                                    </WidthLimiter>
                                </div>
                            )}
                            sorter
                            width={300}
                            align="left"
                        />
                        <Table.Column
                            dataIndex="registration_certificate_number"
                            key="moh_application_number"
                            title={t('applications.fields.moz_data')}
                            render={(value, row: any) => {
                                const dateSuffix = t(
                                    'applications.wizard.application_date_suffix'
                                );
                                const val = value
                                    ? joinObjectValues(
                                          row,
                                          [
                                              'registration_certificate_number',
                                              'registration_certificate_date',
                                          ],
                                          ` ${dateSuffix} `
                                      )
                                    : '';

                                return val && <TextField value={val} />;
                            }}
                            sorter
                            width={300}
                            align="left"
                        />
                        <Table.Column
                            dataIndex={['status_pharmasol', 'name']}
                            key="status_pharmasol"
                            title={t('applications.fields.application_status')}
                            render={(value, row: IColumn) => {
                                if (!value) return null;

                                const color =
                                    row?.status_pharmasol?.color ?? undefined;

                                return (
                                    <StatusTagField
                                        value={value}
                                        style={
                                            color
                                                ? { backgroundColor: color }
                                                : undefined
                                        }
                                    />
                                );
                            }}
                            sorter
                            width={200}
                            align="left"
                        />
                        <Table.Column
                            dataIndex={['status', 'name']}
                            key="status_code"
                            title={t('applications.fields.ectd_dossier_status')}
                            render={value =>
                                value ? <TextField value={value} /> : null
                            }
                            sorter
                            align="left"
                        />
                        <Table.Column
                            dataIndex={['type', 'name']}
                            key="type_id"
                            title={t('applications.fields.procedure')}
                            sorter
                            align="left"
                        />
                        <Table.Column
                            dataIndex="created_at"
                            key="created_at"
                            title={t('applications.fields.created_at')}
                            sorter
                            align="left"
                        />
                        <Table.Column
                            title={t('table.actions')}
                            dataIndex="actions"
                            render={(_, record: any) => {
                                let itemId = record.id;

                                return (
                                    <EditButton
                                        hideText
                                        size="small"
                                        recordItemId={itemId}
                                    />
                                );
                            }}
                            align="center"
                        />
                    </Table>
                    {pagination && totalItems > 0 ? (
                        <TablePagination
                            currentPage={currentPage}
                            pageSize={resolvedPageSize}
                            totalItems={totalItems}
                            pageSizeOptions={orderedPageSizeOptions}
                            onChangePage={handlePageChange}
                            onChangePageSize={handlePageSizeChange}
                            pageSizeSelectorWidth={
                                resolvedPageSizeSelectorWidth
                            }
                            previousLabel={t(
                                'applications.pagination.previous'
                            )}
                            nextLabel={t('applications.pagination.next')}
                            showDocumentsLabel={t(
                                'applications.pagination.show_documents'
                            )}
                            rangeLabel={(start, end, total) =>
                                `${start}-${end} ${t(
                                    'applications.pagination.of_total'
                                )} ${total}`
                            }
                        />
                    ) : null}
                </div>
            </div>
        </ListLayout>
    );
};
